import React, { useState, useRef } from "react";
import { Upload, ScanSearch, Sparkles, Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { analyzeImage, buildVariationPrompt, generateImage, fileToBase64 } from "../utils/apiHelpers";
import ImageCard from "./ImageCard";

export default function WorkflowImage() {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [variationPrompt, setVariationPrompt] = useState("");
  const [variationImage, setVariationImage] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [error, setError] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef();

  const handleFile = (file) => {
    if (!file || !file.type.startsWith("image/")) {
      setError("Please upload a valid image file (JPG, PNG, WEBP).");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Image must be under 10MB.");
      return;
    }
    setError("");
    setUploadedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setCurrentStep(1);
    setAnalysis(null);
    setVariationImage(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleAnalyze = async () => {
    setError("");
    setIsLoading(true);
    setLoadingMsg("🔍 Analyzing your image with Vision AI...");
    try {
      const base64 = await fileToBase64(uploadedFile);
      const result = await analyzeImage(base64);
      setAnalysis(result);
      const prompt = buildVariationPrompt(result);
      setVariationPrompt(prompt);
      setCurrentStep(2);
    } catch (err) {
      setError(`Analysis failed: ${err.message}`);
    } finally {
      setIsLoading(false);
      setLoadingMsg("");
    }
  };

  const handleGenerateVariation = async () => {
    setError("");
    setIsLoading(true);
    setLoadingMsg("✨ Generating your stylistic variation...");
    try {
      const imageUrl = await generateImage(variationPrompt);
      setVariationImage(imageUrl);
      setCurrentStep(3);
    } catch (err) {
      setError(`Variation generation failed: ${err.message}`);
    } finally {
      setIsLoading(false);
      setLoadingMsg("");
    }
  };

  const handleReset = () => {
    setUploadedFile(null);
    setPreviewUrl(null);
    setAnalysis(null);
    setVariationPrompt("");
    setVariationImage(null);
    setCurrentStep(0);
    setError("");
  };

  return (
    <div className="workflow-container">
      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="loading-bar">
          <Loader2 size={18} className="spin" />
          <span>{loadingMsg}</span>
        </div>
      )}

      {/* Step 1: Upload */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-number">01</span>
          <div>
            <h3 className="panel-title">Upload Your Image</h3>
            <p className="panel-sub">JPG, PNG or WEBP — max 10MB</p>
          </div>
        </div>

        <div
          className={`dropzone ${isDragOver ? "drag-over" : ""} ${previewUrl ? "has-file" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current.click()}
        >
          {previewUrl ? (
            <div className="preview-wrapper">
              <img src={previewUrl} alt="Uploaded" className="preview-img" />
              <div className="preview-overlay">
                <CheckCircle size={32} color="#22c55e" />
                <span>Click to change image</span>
              </div>
            </div>
          ) : (
            <div className="dropzone-content">
              <Upload size={36} className="upload-icon" />
              <p className="dropzone-title">Drop image here</p>
              <p className="dropzone-sub">or click to browse files</p>
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => handleFile(e.target.files[0])}
        />

        {currentStep >= 1 && (
          <button
            className="btn btn-primary"
            onClick={handleAnalyze}
            disabled={isLoading}
            style={{ marginTop: "1rem" }}
          >
            <ScanSearch size={16} />
            Analyze with Vision AI
          </button>
        )}
      </div>

      {/* Step 2: Analysis Results */}
      {currentStep >= 2 && analysis && (
        <div className="panel panel-glow">
          <div className="panel-header">
            <span className="panel-number">02</span>
            <div>
              <h3 className="panel-title">Image Analysis</h3>
              <p className="panel-sub">What AI detected in your image</p>
            </div>
          </div>

          <div className="analysis-grid">
            <div className="analysis-item">
              <span className="analysis-key">Main Subject</span>
              <span className="analysis-val">{analysis.mainSubject}</span>
            </div>
            <div className="analysis-item">
              <span className="analysis-key">Artistic Style</span>
              <span className="analysis-val">{analysis.artisticStyle}</span>
            </div>
            <div className="analysis-item">
              <span className="analysis-key">Lighting</span>
              <span className="analysis-val">{analysis.lightingStyle}</span>
            </div>
            <div className="analysis-item">
              <span className="analysis-key">Mood</span>
              <span className="analysis-val">{analysis.mood}</span>
            </div>
            {analysis.colorPalette && (
              <div className="analysis-item full">
                <span className="analysis-key">Color Palette</span>
                <div className="color-chips">
                  {analysis.colorPalette.map((c, i) => (
                    <span key={i} className="color-chip">{c}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="analysis-item full">
              <span className="analysis-key">Composition</span>
              <span className="analysis-val">{analysis.compositionNotes}</span>
            </div>
          </div>

          <div className="panel-header" style={{ marginTop: "1.5rem" }}>
            <span className="panel-number">03</span>
            <div>
              <h3 className="panel-title">Variation Prompt</h3>
              <p className="panel-sub">Edit this prompt before generating</p>
            </div>
          </div>
          <textarea
            className="input-area enhanced"
            rows={4}
            value={variationPrompt}
            onChange={(e) => setVariationPrompt(e.target.value)}
            disabled={isLoading}
          />

          <div className="btn-row">
            <button
              className="btn btn-primary"
              onClick={handleGenerateVariation}
              disabled={isLoading || !variationPrompt.trim()}
            >
              <Sparkles size={16} />
              Generate Variation
            </button>
            <button className="btn btn-ghost" onClick={handleReset}>
              Start Over
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Variation Result */}
      {currentStep >= 3 && variationImage && (
        <div className="panel panel-result">
          <div className="panel-header">
            <span className="panel-number">04</span>
            <div>
              <h3 className="panel-title">Stylistic Variation</h3>
              <p className="panel-sub">AI-generated based on your original image</p>
            </div>
          </div>

          <div className="comparison-grid">
            <div>
              <p className="compare-label">Original</p>
              <img src={previewUrl} alt="Original" className="compare-img" />
            </div>
            <div>
              <p className="compare-label">AI Variation</p>
              <img src={variationImage} alt="Variation" className="compare-img" />
            </div>
          </div>

          <ImageCard
            imageUrl={variationImage}
            title="Style Lab Variation"
            prompt={variationPrompt}
          />
          <button className="btn btn-ghost" onClick={handleReset} style={{ marginTop: "1rem" }}>
            Try Another Image
          </button>
        </div>
      )}
    </div>
  );
}
